import { ReactElement, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../store";
import { useAppCrmEventBriefsRetrieveQuery } from "../../../../store/cabinetApi/generated/crm";
import { skipToken } from "@reduxjs/toolkit/query";
import { useConfirmationModalWithUI } from "../../../Common/ConfirmationModal";
import EventDetailPopover from "./EventDetailPopover";
import {
  useAppSchedulingCalendarEventsPartialUpdateMutation,
  useAppSchedulingCalendarEventsRetrieveQuery,
  useAppSchedulingCalendarEventsDestroyMutation,
  useAppSchedulingMeetingsRestartFromCalendarEventCreateMutation,
} from "../../../../store/cabinetApi/generated/scheduling";
import { scheduling } from "../../../../store/cabinetApi/scheduling";
import {
  selectCalendarLookupByCalendarId, selectConsolidatedEventFromRTK, selectLeaderLookup
} from "../../../../store/schedule/selectors";


export interface Props {
  open: boolean;
  onClosePopover: () => void;
  popoverAnchorEl: Element | null;
  lookupKey: string;
  onOpenMeeting?: (meetingId: number) => void;
  onUpdateActiveBrief?: React.Dispatch<React.SetStateAction<{
    eventUniqueId: string; generate?: boolean; leaderId?: number; calendarId?: number
  } | null>>;
}

export const EventDetailPopoverContainer = ({
  open, onClosePopover, popoverAnchorEl, lookupKey,
  onOpenMeeting,
  onUpdateActiveBrief
}: Props): ReactElement => {
  const dispatch = useDispatch();
  const { confirm: confirmReschedule, ConfirmationModalComponent: RescheduleConfirmationModal } =
    useConfirmationModalWithUI();
  const leaderLookup = useSelector(selectLeaderLookup);
  const calendarLookup = useSelector(selectCalendarLookupByCalendarId);
  const { data: event, isLoading: eventLoading } = useAppSchedulingCalendarEventsRetrieveQuery(lookupKey, {
    selectFromResult: ({ data, ...other }) => ({
      ...other,
      data: data ? selectConsolidatedEventFromRTK(leaderLookup, calendarLookup, [data]) : undefined,
    })
  });

  const [updateEvent] = useAppSchedulingCalendarEventsPartialUpdateMutation();
  const [deleteEvent] = useAppSchedulingCalendarEventsDestroyMutation();
  const [restartFromCalendarEvent, { isLoading: isRestarting }] =
    useAppSchedulingMeetingsRestartFromCalendarEventCreateMutation();

  const user = useSelector((state: RootState) => state.auth.user);
  const leaderInfo = event?.leaderInfo || [];
  const calendarInfo = event?.calendarInfo || [];

  const uniqueId = event?.unique_id;
  
  const {
    data: briefData, isLoading: briefLoading, error: briefError,
  } = useAppCrmEventBriefsRetrieveQuery(open && uniqueId ? uniqueId : skipToken);
  
  const handleShowBrief = () => {
    if (onUpdateActiveBrief && uniqueId) {
      onUpdateActiveBrief({
        eventUniqueId: uniqueId,
        generate: false
      });
    }
    onClosePopover();
  };

  const handlePrepareBrief = () => {
    if (!leaderInfo?.[0]?.id || !calendarInfo?.[0]?.id) return;
    if (onUpdateActiveBrief && uniqueId) {
      onUpdateActiveBrief({
        eventUniqueId: uniqueId,
        generate: true,
        leaderId: event.leaderInfo?.[0].id,
        calendarId: calendarInfo[0].id
      });
    }
  };

  const handleUpdateEvent = async (title: string, newStartTime: string, newEndTime: string) => {
    await updateEvent({
      id: lookupKey,
      patchedCalendarEvent: {
        title,
        start: newStartTime,
        end: newEndTime,
      }
    });
  };

  const handleCancelEvent = async () => {
    await deleteEvent(lookupKey);
    onClosePopover();
  };

  const handleReschedule = async () => {
    if (!event?.id || !event?.calendarId) return;
    const confirmed = await confirmReschedule({
      title: "Cancel and propose new times",
      message:
        "This calendar event will be deleted from your calendar. " +
        "The related meeting will be moved to a pending state so you can propose new times.",
      confirmText: "Continue",
      cancelText: "Cancel",
      isDestructive: true,
    });
    if (!confirmed) return;
    try {
      const meeting = await restartFromCalendarEvent({
        eventId: event.id,
        calendarId: event.calendarId,
      }).unwrap();
      dispatch(scheduling.util.invalidateTags(["CalendarEvent"]));
      onOpenMeeting?.(meeting.id);
      onClosePopover();
    } catch (e) {
      console.error("restart-from-calendar-event failed", e);
    }
  };

  const canEditCalendarEvent = !!event?.editable;
  const canEditEvent = !!user?.features.CALENDAR_EVENT_EDITING && canEditCalendarEvent;
  const showReschedule = !!(canEditCalendarEvent && onOpenMeeting && event?.id && event?.calendarId);

  // Determine which button to show based on brief data and error state
  const shouldShowPrepareBrief = useMemo(() => {
    // Show "Prepare Brief" if user has the feature, not loading, and either no data or 404 error
    return !!(user?.features.MEETING_BRIEFS && !briefLoading && 
      (!briefData ||
        (briefError && typeof briefError === 'object' && 'status' in briefError && briefError.status === 404)
      ));
  }, [user?.features.MEETING_BRIEFS, briefLoading, briefData, briefError]);

  const shouldShowShowBrief = useMemo(() => {
    return !!(user?.features.MEETING_BRIEFS && briefData);
  }, [user?.features.MEETING_BRIEFS, briefData]);

  return (
    <>
      <EventDetailPopover
        open={open}
        onClosePopover={onClosePopover}
        popoverAnchorEl={popoverAnchorEl}
        event={event || undefined}
        eventLoading={eventLoading}
        calendarInfo={calendarInfo}
        briefLoading={briefLoading}
        shouldShowShowBrief={shouldShowShowBrief}
        shouldShowPrepareBrief={shouldShowPrepareBrief}
        canEditEvent={canEditEvent}
        handleShowBrief={handleShowBrief}
        handlePrepareBrief={handlePrepareBrief}
        handleUpdateEvent={handleUpdateEvent}
        handleCancelEvent={handleCancelEvent}
        handleReschedule={showReschedule ? handleReschedule : undefined}
        rescheduleLoading={isRestarting}
        showReschedule={showReschedule}
      />
      <RescheduleConfirmationModal />
    </>
  );
};

export default EventDetailPopoverContainer; 
